import React, { createContext, useContext, useState, ReactNode } from 'react';
import { API_BASE_URL } from '../config/api';

interface User {
  id: string;
  email: string;
  name: string;
  mobileNumber: string;
  membershipType: 'basic' | 'premium' | 'enterprise';
}

interface AuthContextType {
  user: User | null;
  login: (identifier: string, otp: string) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  isLoading: boolean;
  token: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);

  // Restore session on app load
  React.useEffect(() => {
    const savedToken = localStorage.getItem('access_token');
    const savedUser = localStorage.getItem('user');
    
    if (savedToken && savedUser) {
      try {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
      } catch (error) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (identifier: string, otp: string) => {
    setIsLoading(true);
    try {
      console.log('🔐 Verifying OTP:', { identifier, otp_code: otp });
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, otp_code: otp })
      });
      console.log('📡 Verify response:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Invalid OTP or OTP expired');
      }
      
      const data = await response.json();
      const user: User = {
        id: data.user.id,
        email: data.user.email || `${identifier}@phone.com`,
        name: data.user.name,
        mobileNumber: data.user.mobile_number,
        membershipType: data.user.membership_type || 'basic'
      };
      
      // Store token and user
      setToken(data.access_token);
      setUser(user);
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('user', JSON.stringify(user));
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const logout = () => {
    console.log('🔓 Logging out user');
    setUser(null);
    setToken(null);
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    // Clear any other app-specific localStorage items
    localStorage.removeItem('redirectToAccount');
    
    // Navigate to home and clear browser history
    window.history.replaceState({ page: 'home' }, '');
    
    // Force a page reload to ensure clean state
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, isLoading, token }}>
      {children}
    </AuthContext.Provider>
  );
};